import React from "react";
import { TwitterOutlined, DiscordOutlined } from "@ant-design/icons";

import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "./components/ui/card";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "./components/ui/accordion";
import { Button } from "./components/ui/button";

import faqs from "./faq.json";
import features from "./features.json";
import plans from "./plans.json";


const App: React.FC = () => {
  return (
    <div
      className="min-h-screen text-slate-50"
      style={{
        backgroundImage: "url('/bg.jpg')",
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      {/* Dark overlay for readability */}
      <div className="min-h-screen bg-slate-950/80">
        <div className="mx-auto flex min-h-screen max-w-6xl flex-col px-4 pb-8 pt-4">
          {/* NAVBAR */}
          <header className="border-b border-white/10">
            <div className="flex items-center justify-between py-4">
              {/* Logo */}
              <div className="flex items-center">
                <img
                  src="/atom-logo.png"
                  alt="Atom AIO"
                  className="h-14 w-auto md:h-16"
                />
              </div>

              {/* Nav + CTA */}
              <nav className="flex items-center gap-8 text-sm">
                <a
                  href="#features"
                  className="text-slate-200 transition-colors hover:text-white"
                >
                  Features
                </a>
                <a
                  href="#pricing"
                  className="text-slate-200 transition-colors hover:text-white"
                >
                  Pricing
                </a>

                <a
                  href="#faq"
                  className="text-slate-200 transition-colors hover:text-white"
                >
                  FAQ&apos;s
                </a>

                <Button
                  asChild
                  className="rounded-full bg-[#4ade80] px-5 py-2 text-sm font-semibold text-slate-900 shadow-md hover:bg-[#22c55e] border-none"
                >
                  <a href="https://app.atomaio.xyz/" rel="noreferrer">
                    Open app
                  </a>
                </Button>
              </nav>
            </div>
          </header>

          {/* MAIN CONTENT */}
          <main className="flex-1">
            {/* HERO SECTION */}
            <section className="mt-12 grid gap-8 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] md:items-center">
              <div>
                <h1 className="font-heading text-4xl font-bold leading-tight text-slate-50 md:text-5xl md:leading-tight">
                  Automated Trading Experience
                </h1>

                <p className="mt-6 max-w-xl text-sm leading-relaxed text-slate-200 md:text-base">
                  Atom AIO is a Solana-native automation suite built around
                  Magic Eden. We continuously monitor your registered
                  collections so you can react before the floor moves.
                </p>

                <p className="mt-4 max-w-xl text-sm leading-relaxed text-slate-200/90 md:text-base">
                  Access is fully subscription-based – no NFT mint, no secondary
                  passes. Three SOL-priced packages scale with your volume and
                  unlock our on-chain automation tools.
                </p>

                <p className="mt-4 max-w-xl text-xs leading-relaxed text-slate-400 md:text-sm">
                  Our stack is tuned for HFT-style trading: real-time collection
                  monitoring, smart alerts, and streamlined flows for capturing
                  listing misprices and cross-collection arbitrage opportunities.
                  Subscriptions are paid in SOL through our Discord renewal bot.
                </p>
              </div>

              {/* HERO RIGHT CARD – GIF */}
              <div className="relative mx-auto flex h-64 w-full max-w-md items-center justify-center overflow-hidden rounded-[32px] bg-slate-900/80 shadow-2xl shadow-emerald-500/25 ring-1 ring-emerald-500/40">
                <img
                  src="/mintgif.gif"
                  alt="Atom AIO visualization"
                  className="h-full w-full object-cover"
                />
                <div className="pointer-events-none absolute inset-0 rounded-[32px] bg-gradient-to-tr from-emerald-400/20 via-transparent to-cyan-400/20" />
              </div>
            </section>

            {/* FEATURE STRIP */}
            <section id="features" className="mt-16 grid gap-4 md:grid-cols-3">
              {features.map((f) => (
                <Card
                  key={f.title}
                  className="rounded-2xl border border-slate-700/70 bg-slate-900/60 shadow-lg shadow-black/30"
                >
                  <CardHeader className="p-4 pb-2">
                    <CardTitle className="font-heading text-sm text-slate-100">
                      {f.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 pb-4 pt-0">
                    <p className="text-xs leading-relaxed text-slate-400">
                      {f.body}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </section>

            {/* PRICING */}
            <section id="pricing" className="mt-16">
              <h2 className="text-center font-heading text-2xl font-bold text-slate-50">
                Subscription Plans
              </h2>
              <p className="mt-2 text-center text-xs text-slate-400">
                Pay in SOL through our Discord subscription bot. Instant access
                on confirmation.
              </p>

              <div className="mt-8 grid gap-4 md:grid-cols-3">
                {plans.map((pkg) => (
                  <Card
                    key={pkg.name}
                    className="flex flex-col rounded-3xl border border-emerald-500/40 bg-gradient-to-b from-slate-900/90 to-slate-950/90 p-5 shadow-xl shadow-black/60"
                  >
                    <CardHeader className="p-0">
                      <CardTitle className="mt-2 font-heading text-lg text-slate-50">
                        {pkg.name}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0 pt-4">
                      <div className="space-y-1 text-xs text-slate-200">
                        <p>
                          Net Amount: <strong>{pkg.netAmount}</strong>
                        </p>
                        <p>
                          Duration: <strong>{pkg.duration}</strong>
                        </p>
                        <p>
                          Discount: <strong>{pkg.discount}</strong>
                        </p>
                        <p>
                          Total Price: <strong>{pkg.totalPrice}</strong>
                        </p>
                      </div>

                      <p className="mt-4 text-xs leading-relaxed text-slate-400">
                        {pkg.blurb}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* single global CTA */}
              <div className="mt-8 flex justify-center">
                <Button
                  asChild
                  className="rounded-full bg-slate-50 px-8 py-3 text-sm font-heading text-slate-950 shadow-lg shadow-emerald-500/30 hover:brightness-105 border-none"
                >
                  <a
                    href="https://discord.gg/bHfayU9F48" // TODO: real link
                    target="_blank"
                    rel="noreferrer"
                  >
                    Subscribe now via Discord
                  </a>
                </Button>
              </div>
            </section>

            {/* FAQ SECTION */}
            <section id="faq" className="mt-16">
              <h2 className="text-lg font-bold text-slate-50">FAQ</h2>
              <p className="mt-2 text-sm text-slate-400">
                Quick answers to the most common questions about Atom AIO.
              </p>

              <Accordion
                type="single"
                collapsible
                defaultValue="item-0"
                className="mt-6 space-y-3"
              >
                {faqs.map((faq, index) => (
                  <AccordionItem
                    key={faq.question}
                    value={`item-${index}`}
                    className="rounded-2xl border border-slate-800 bg-slate-900/70 px-4 shadow-md shadow-black/30"
                  >
                    <AccordionTrigger className="flex w-full items-center justify-between gap-4 py-3 text-left text-sm font-medium text-slate-100 hover:no-underline">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="pb-4 text-sm leading-relaxed text-slate-300">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          </main>

          {/* FOOTER */}
          <footer className="mt-12 border-t border-slate-800 pt-4 text-[11px] text-slate-500">
            <div className="flex flex-col items-center justify-between gap-3 md:flex-row">
              {/* Left: docs + socials */}
              <div className="flex items-center gap-4">
                <a
                  href="https://atomaio.gitbook.io/atom-aio-v1.0/"
                  className="text-slate-400 transition-colors hover:text-slate-200"
                >
                  Docs
                </a>

                <a
                  href="https://twitter.com/Atom_AIO" // TODO
                  target="_blank"
                  rel="noreferrer"
                  className="text-slate-400 transition-colors hover:text-slate-200"
                >
                  <TwitterOutlined className="text-base" />
                </a>

                <a
                  href="https://discord.gg/bHfayU9F48" // TODO
                  target="_blank"
                  rel="noreferrer"
                  className="text-slate-400 transition-colors hover:text-slate-200"
                >
                  <DiscordOutlined className="text-base" />
                </a>
              </div>

              {/* Right: text */}
              <div className="text-center md:text-right">
                <p>All rights reserved by Atom AIO</p>
                <p className="mt-1">
                  Copyright © {new Date().getFullYear()}.
                </p>
              </div>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default App;